import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { formatFieldName } from "utils";
import { formatTokenCount } from "./formatters";
import { JsonTree } from "./JsonTree";

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export const AnalysisFieldSelector = ({
  availableFields, selectedFields, allFieldsSelected,
  estimatedTokenCount, selectedSessionCount,
  onToggleField, onSelectAllFields,
  previewData,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const previewJson = useMemo(() => {
    if (!previewData) return "";
    try {
      return JSON.stringify(previewData, null, 2);
    } catch {
      return "";
    }
  }, [previewData]);

  const previewSize = previewJson.length;
  const hasPreview = Array.isArray(previewData) && previewData.length > 0 && previewSize > 0;

  const handleCopy = async () => {
    if (!previewJson) return;
    try {
      await navigator.clipboard.writeText(previewJson);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <Card>
      <Header>
        <div>
          <Title>Analysis Fields</Title>
          <Subtitle>Select which data fields to include in the report context</Subtitle>
        </div>
        <Form.Check
          type="checkbox"
          id="select-all-fields"
          label="Select All"
          checked={allFieldsSelected}
          onChange={(e) => onSelectAllFields(e.target.checked)}
        />
      </Header>
      <FieldList>
        {availableFields.map((field) => (
          <FieldChip
            key={field}
            onClick={() => onToggleField(field)}
            $selected={selectedFields[field]}
          >
            {selectedFields[field] ? "✓ " : ""}{formatFieldName(field)}
          </FieldChip>
        ))}
      </FieldList>
      <ContextBar>
        <span>
          Context size: <Strong>{formatTokenCount(estimatedTokenCount)} tokens</Strong>
        </span>
        <span>
          {availableFields.filter((f) => selectedFields[f]).length}/{availableFields.length} fields selected
          {" · "}
          {selectedSessionCount} session{selectedSessionCount !== 1 ? "s" : ""}
        </span>
      </ContextBar>

      {previewData !== undefined && (
        <PreviewWrapper>
          <PreviewHeader
            type="button"
            onClick={() => setPreviewOpen((v) => !v)}
            disabled={!hasPreview}
          >
            <span>
              Inspect JSON context
              {hasPreview ? (
                <PreviewHint>
                  {" "}· {formatBytes(previewSize)} · {previewData.length} session
                  {previewData.length === 1 ? "" : "s"}
                </PreviewHint>
              ) : (
                <PreviewHint> (select at least one session and one field)</PreviewHint>
              )}
            </span>
            <FontAwesomeIcon
              icon={faChevronDown}
              style={{
                transform: previewOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </PreviewHeader>
          {previewOpen && hasPreview && (
            <PreviewBody>
              <PreviewToolbar>
                <CopyButton type="button" onClick={handleCopy}>
                  {copied ? "Copied!" : "Copy JSON"}
                </CopyButton>
              </PreviewToolbar>
              <PreviewTreeShell>
                <JsonTree data={previewData} defaultExpandDepth={2} />
              </PreviewTreeShell>
            </PreviewBody>
          )}
        </PreviewWrapper>
      )}
    </Card>
  );
};

const Card = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0px 1px 2px 0px rgba(14, 26, 49, 0.05);
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h5`
  margin: 0;
  color: ${colors.nightlyWoods900};
`;

const Subtitle = styled.span`
  font-size: 13px;
  color: #6c757d;
`;

const FieldList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const FieldChip = styled.div`
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  border: 1px solid ${({ $selected }) => $selected ? colors.nightlyWoods500 : colors.milkGlass600};
  background-color: ${({ $selected }) => $selected ? colors.nightlyWoods500 : "white"};
  color: ${({ $selected }) => $selected ? "white" : colors.nightlyWoods900};
  transition: all 0.15s ease;
`;

const ContextBar = styled.div`
  margin-top: 16px;
  padding: 10px 16px;
  background-color: ${colors.milkGlass200};
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #6c757d;
`;

const Strong = styled.strong`
  color: ${colors.nightlyWoods900};
`;

const PreviewWrapper = styled.div`
  margin-top: 16px;
  border: 1px solid ${colors.milkGlass600};
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${colors.milkGlass200};
  border: none;
  padding: 10px 14px;
  font-family: "Inter", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const PreviewHint = styled.span`
  color: ${colors.milkGlass950};
  font-weight: 400;
  font-size: 12px;
`;

const PreviewBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: white;
  border-top: 1px solid ${colors.milkGlass600};
`;

const PreviewToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CopyButton = styled.button`
  background: white;
  color: ${colors.blue500};
  border: 1px solid ${colors.blue300};
  border-radius: 6px;
  padding: 4px 12px;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${colors.blue50};
  }
`;

const PreviewTreeShell = styled.div`
  padding: 12px;
  background: ${colors.milkGlass500};
  border: 1px solid ${colors.milkGlass600};
  border-radius: 6px;
  max-height: 400px;
  overflow: auto;
`;
